import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  SimpleChanges,
  ViewChild,
  HostBinding,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { IColumnDefinition } from '../types';
import { DomSanitizer } from '@angular/platform-browser';
import { SafePipe } from '@omni/pipes/safe.pipe';

@Component({
  selector: 'virtual-table',
  standalone: true,
  imports: [CommonModule, IonicModule, SafePipe],
  templateUrl: './virtual-table.component.html',
  styleUrls: ['./virtual-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualTableComponent implements AfterViewInit, OnDestroy {
  @Input() columns: IColumnDefinition[] = [];
  @Input() data: any[] = [];
  private _rowClickable: boolean = false;
  @Input() set rowClickable(value: boolean) {
    if (value === this._rowClickable) return;
    this._rowClickable = value;
    this.cdr.markForCheck();
  }
  get rowClickable(): boolean {
    return this._rowClickable;
  }
  private _loadingMore = false;
  @Input() set loadingMore(value: boolean) {
    if (value === this._loadingMore) return;
    this._loadingMore = value;
    this.cdr.markForCheck();
  }
  get loadingMore(): boolean {
    return this._loadingMore;
  }
  private _hasMoreData = true;
  @Input() set hasMoreData(value: boolean) {
    if (value === this._hasMoreData) return;
    this._hasMoreData = value;
    this.cdr.markForCheck();
  }
  get hasMoreData(): boolean {
    return this._hasMoreData;
  }
  public actionColumnWidth = 110;
  @Input() showAction = true;
  @Input() showCheckbox = false;
  /** When false (and showCheckbox is true), the per-row checkboxes still
   *  render but the header's select-all checkbox is hidden. Use for
   *  single-select tables where 'select all' has no meaning. */
  @Input() showSelectAll = true;
  public checkboxColumnWidth = 40;
  @Input() selectedRows = new Set<string>();
  @Input() highlightedRows = new Set<string>();
  private _primaryKeyField = 'id';
  @Input() set primaryKeyField(value: string) {
    if (value === this._primaryKeyField) return;
    this._primaryKeyField = value;
    this.cdr.markForCheck();
  }
  get primaryKeyField(): string {
    return this._primaryKeyField;
  }
  private _selectAll?: boolean;
  @Input() set selectAll(value: boolean) {
    if (value === this._selectAll) return;
    this._selectAll = value;
    this.cdr.markForCheck();
  }
  get selectAll(): boolean {
    return this._selectAll;
  }
  @Input() sorted: { field: string; asc: boolean } | null = null;
  @Input() noDataText: string = 'No data';
  /** When true, each data row is native-HTML5-draggable. A single delegated
   *  dragstart listener on the container emits onRowDragStart — no per-row
   *  binding, no listener leak under virtual-scroll recycle. */
  @Input() rowDraggable = false;

  @Output() onRowSelected = new EventEmitter<{ row: any; checked: boolean }>();
  @Output() onRowClicked = new EventEmitter<{ row: any }>();
  @Output() onSelectAll = new EventEmitter<boolean>();
  @Output() onLoadMore = new EventEmitter<void>();
  @Output() onSort = new EventEmitter<{
    column: IColumnDefinition;
    asc: boolean;
  }>();
  @Output() onColumnsModified = new EventEmitter<IColumnDefinition[]>();
  @Output() onRowDragStart = new EventEmitter<{ row: any; event: DragEvent }>();

  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLDivElement>;
  @ContentChild('actionCellTemplate', { read: TemplateRef })
  actionCellTemplate!: TemplateRef<any>;
  @ContentChild('cellTemplate', { read: TemplateRef })
  _cellTemplate!: TemplateRef<any>;

  private readonly _rowHeight = 40;
  private _isResizing = false;
  private _resizeObserver: ResizeObserver | null = null;
  private _scrollAnimationFrameId: number | null = null;
  private readonly _skeletonRowCount = 3;
  private readonly _skeletonRows = Array(this._skeletonRowCount)
    .fill(0)
    .map((_, index) => ({ id: '__skelton-' + index }));

  visibleRows: any[] = [];
  paddingTop = 0;
  paddingBottom = 0;
  dragOverColumnIndex: number | null = null;
  dragStartIndex: number | null = null;

  stickyLeftOffsets: number[] = [];
  stickyRightOffsets: number[] = [];
  fillerNeeded = false;

  totalTableWidth: number = 0;

  get skeletonRows() {
    return this.hasMoreData ? this._skeletonRows : [];
  }

  @HostBinding('style.cursor') get cursor(): string {
    if (this._isResizing) {
      return 'col-resize';
    }
    if (this.loadingMore) {
      return 'wait';
    }
    return 'default';
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.data = changes['data'].currentValue;
      this.calculateVisibleRows();
    }
    if (changes['columns']) {
      this.columns = changes['columns'].currentValue;
      this.calculateColumns();
    }
  }

  ngOnDestroy(): void {
    this._scrollAnimationFrameId = null;
    this._resizeObserver?.disconnect();
  }

  ngAfterViewInit(): void {
    this.calculateColumns();
    this.cdr.markForCheck();

    let resizeObserverTimeout: NodeJS.Timeout | null = null;
    this._resizeObserver = new ResizeObserver(() => {
      if (resizeObserverTimeout) {
        clearTimeout(resizeObserverTimeout);
      }
      resizeObserverTimeout = setTimeout(() => {
        this.calculateColumns();
        this.calculateVisibleRows();
        this.cdr.markForCheck();
      }, 100);
    });
    this._resizeObserver.observe(this.tableContainer.nativeElement);
  }

  calculateColumns(): void {
    const checkboxColumnWidth = this.showCheckbox ? this.checkboxColumnWidth : 0;
    const actionColumnWidth = this.showAction ? this.actionColumnWidth : 0;
    let left = checkboxColumnWidth;
    let right = actionColumnWidth;
    this.totalTableWidth =
      checkboxColumnWidth + // checkbox
      actionColumnWidth + // action
      this.columns.reduce((sum, c) => sum + c.width, 0);
    this.fillerNeeded =
      this.totalTableWidth < this.tableContainer?.nativeElement?.offsetWidth;

    this.stickyLeftOffsets = this.columns.map((col) => {
      if (col.sticky === 'left') {
        const offset = left;
        left += col.width;
        return offset;
      }
      return null;
    });

    this.stickyRightOffsets = this.columns
      .slice()
      .reverse()
      .map((col) => {
        if (col.sticky === 'right') {
          const offset = right;
          right += col.width;
          return offset;
        }
        return null;
      });
  }

  onScroll(): void {
    if (this.data.length === 0) return;
    this.calculateVisibleRows();
  }

  calculateVisibleRows(force: boolean = false): void {
    const calculate = () => {
      const container = this.tableContainer?.nativeElement;
      if (!container) return;

      const buffer = 2;
      const scrollTop = this.tableContainer.nativeElement.scrollTop;
      const containerHeight = this.tableContainer.nativeElement.clientHeight;
      const totalRows = this.data.length;

      const visibleCount =
        Math.ceil(containerHeight / this._rowHeight) + buffer;
      const startIndex = Math.max(
        0,
        Math.floor(scrollTop / this._rowHeight) - buffer,
      );
      const endIndex = Math.min(totalRows, startIndex + visibleCount);

      this.visibleRows = this.data.slice(startIndex, endIndex);

      const extraSkeletonRows = this.hasMoreData ? this._skeletonRowCount : 0;

      this.paddingTop = startIndex * this._rowHeight;
      this.paddingBottom =
        Math.max(0, totalRows - endIndex - extraSkeletonRows) * this._rowHeight;

      this.cdr.markForCheck();
      // Trigger infinite scroll if we're near the end and not done
      const nearEnd = endIndex >= totalRows;
      if (nearEnd && this.hasMoreData && !this.loadingMore) {
        this.loadingMore = true;
        this.cdr.markForCheck();
        this.onLoadMore.emit();
      }
    };

    if (force) {
      calculate();
    } else {
      if (this._scrollAnimationFrameId) {
        cancelAnimationFrame(this._scrollAnimationFrameId);
      }
      this._scrollAnimationFrameId = requestAnimationFrame(() => {
        calculate();
      });
    }
  }

  handleSort(column: IColumnDefinition, event: MouseEvent): void {
    if (!column.sortable) return;
    if (
      event.target instanceof HTMLDivElement &&
      event.target.classList.contains('column-header-resize-handle')
    ) {
      return;
    }
    if (this.sorted?.field === column.field) {
      if (this.sorted.asc) {
        this.onSort.emit({ column, asc: false });
      } else {
        this.onSort.emit(undefined);
      }
    } else {
      this.onSort.emit({ column, asc: true });
    }
  }

  onResizeStart(event: MouseEvent | TouchEvent, colIndex: number): void {
    this._isResizing = true;
    const isTouch = event.type.startsWith('touch');
    const startX = isTouch
      ? (event as TouchEvent).touches[0].clientX
      : (event as MouseEvent).clientX;

    const startWidth = this.columns[colIndex].width;
    let animationFrameId: number;
    const resizeColumn = (colIndex: number, width: number) => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(() => {
        this.columns[colIndex].width = width;
        this.calculateColumns();
        this.cdr.markForCheck();
      });
    };

    const onMove = (e: MouseEvent | TouchEvent) => {
      this._isResizing = true;
      const currentX =
        e instanceof TouchEvent ? e.touches[0].clientX : e.clientX;
      const delta = currentX - startX;
      const newWidth = startWidth + delta;
      if (newWidth > 30) {
        resizeColumn(colIndex, newWidth);
      }
    };

    const updateEventHandlers = (add: boolean) => {
      const fn = add ? document.addEventListener : document.removeEventListener;
      fn('mousemove', onMove);
      fn('mouseup', onEnd);
      fn('touchmove', onMove);
      fn('touchend', onEnd);
    };

    const onEnd = () => {
      updateEventHandlers(false);
      this._isResizing = false;
      this.onColumnsModified.emit(this.columns);
      this.cdr.markForCheck();
    };

    updateEventHandlers(true);
    event.preventDefault();
    this.cdr.markForCheck();
  }

  onDragStart(event: DragEvent, colIndex: number): void {
    this.dragStartIndex = colIndex;
    event.dataTransfer?.setData('text/plain', String(colIndex));
  }

  onDragOver(event: DragEvent, colIndex: number): void {
    // Column-reorder visual feedback. Only active during a header-cell drag
    // (dragStartIndex set by onDragStart). Row→calendar drags bubble through
    // here too — guard against them painting the blur/.drag-over class.
    if (this.dragStartIndex === null) return;
    this.dragOverColumnIndex = colIndex;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'; // Show move cursor
    }
    event.preventDefault();
  }

  onDragEnd(event: DragEvent): void {
    this.dragOverColumnIndex = null;
    this.dragStartIndex = null;
  }

  onDrop(event: DragEvent, colIndex: number): void {
    if (this.dragStartIndex === null) return;
    event.preventDefault();
    const fromIndex = this.dragStartIndex;
    const toIndex = colIndex;
    if (fromIndex !== null && fromIndex !== toIndex) {
      const buffer = [...this.columns];
      const movedColumn = buffer.splice(fromIndex, 1)[0];
      buffer.splice(toIndex, 0, movedColumn);
      this.onColumnsModified.emit(buffer);
    }
    this.dragOverColumnIndex = null;
    this.dragStartIndex = null;
  }

  /** Delegated dragstart handler on the table container. Reads the row index
   *  stashed on the closest [data-row-index] element and emits the bound row.
   *  Single listener for all rows — no per-row binding, immune to virtual-scroll
   *  recycle leaks. */
  handleContainerDragStart(event: DragEvent): void {
    if (!this.rowDraggable) return;
    const target = event.target as HTMLElement | null;
    const rowEl = target?.closest('[data-row-index]') as HTMLElement | null;
    if (!rowEl) return;
    const idx = Number(rowEl.dataset.rowIndex);
    if (!Number.isFinite(idx)) return;
    const row = this.visibleRows[idx];
    if (!row) return;
    this.onRowDragStart.emit({ row, event });
  }

  handleRowSelected(row: any, checked: boolean): void {
    this.onRowSelected.emit({ row, checked });
  }

  handleRowClicked(row: any, event: MouseEvent | TouchEvent): void {
    if (!row || !this.rowClickable) return;
    if (
      (event.target as HTMLElement).closest('.action-cell') ||
      (event.target as HTMLElement).closest('.checkbox-cell')
    )
      return;
    this.onRowClicked.emit(row);
  }

  handleSelectAll(checked: boolean): void {
    this.onSelectAll.emit(checked);
  }

  trackByFn = (index: number, row: any) => {
    return row[this._primaryKeyField];
  };

  trackById(index: number, item: any) { return item?.id || item?.ID || index; }
}
